import { DefaultGameState } from "@shared/types";
import { GameEvent } from "../enums/GameEvent";
import { Page } from "../enums/Page";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService, SwitchPageEvent } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";

/** CSS affecting the {@link InventoryComponent} */
const styles: string = css`
:host {
    font-family: "Onesize";
    display: grid;
    grid-template-columns: 25% 50% 20%;
    justify-content: space-around;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
}

.left-sidebar {
    display: flex;
    box-sizing: border-box;
    flex-direction: column;
    height: 90vh;
    width: 100%;
    margin-top: 10%;
    border-radius: 12px;
    padding: 10px;
    background: rgba(41, 35, 35, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);
}
`;

/** Default page used by the {@link RootComponent} */
export const DefaultPage: Page = Page.Canvas;

/**
 * A "router" Web Component
 *
 * @remarks Will listen for the Switch Page event and render the requested page if possible. Otherwise, switches to a Not Found page.
 */
export class RootComponent extends HTMLElement {
    private readonly _gameEventService: GameEventService = new GameEventService();
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private _currentPage: Page = DefaultPage;
    private _currentGameState?: DefaultGameState;
    private _username?: string;
    private _gameStartTime?: number;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Restore username from localStorage
        const storedUsername: string | null = localStorage.getItem("username");
        if (storedUsername) {
            this._username = storedUsername;
            console.log("Restored username from localStorage:", this._username);
        }

        this.addEventListener("username-submitted", ((event: Event) => {
            // eslint-disable-next-line @typescript-eslint/typedef
            const customEvent = event as CustomEvent<{ username: string }>;
            this._username = customEvent.detail.username;
            console.log("Username stored:", this._username);
        }) as EventListener);

        void this.render();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: DefaultGameState = await this._gameRouteService.getGameState() as DefaultGameState;
        this._currentGameState = state;

        if (state.roomAlias !== "startup" && !this._gameStartTime) {
            this._gameStartTime = Date.now();
        }
    }

    /**
     * Render the contents of this page
     */
    public async render(): Promise<void> {
        if (!this.shadowRoot) {
            return;
        }
        await this.refreshGameState();

        // Listen for the Switch Page event
        this._gameEventService.addGameEventListener<SwitchPageEvent>(GameEvent.SwitchPage, event => {
            this.handleSwitchPage(event.page);
        });
        let elements: HTMLElement[] = [];
        if (this._currentPage !== Page.Accusate) {
            elements = htmlArray`
            <style>
                ${styles}
            </style>
            
            ${this.renderLeftSidebar()}
            ${this.renderCurrentPage()}
            ${this.renderRightSidebar()}
        `;
        }
        else {
            elements = htmlArray`
            ${this.renderCurrentPage()}
        `;
        }

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the current active page
     *
     * @returns HTML element of the current active page
     */
    private renderCurrentPage(): HTMLElement {
        switch (this._currentPage) {
            case Page.Canvas:
                return html`<game-canvas></game-canvas>`;
            case Page.Accusate:
                console.log("testing accusate page");
                return html`<game-accusate></game-accusate>`;
            default:
                return html`<game-notfound page="${this._currentPage}"></game-notfound>`;
        }
    }

    public renderLeftSidebar(): HTMLElement {
        // If the player is not in the start-up room, meaning they are actively playing the game, then render this:
        if (this._currentGameState!.roomAlias !== "startup") {
            return html`
                <div class="left-sidebar">
                    <game-highscores></game-highscores>
                    <game-inventory></game-inventory>
                </div>
            `;
        }
        // Else if player is in the start-up room, then render this:
        else {
            return html`
            <game-leaderboard></game-leaderboard>
            `;
        }
    }

    private renderRightSidebar(): HTMLElement {
        // If the player is not in the start-up room, meaning they are actively playing the game, then render this:
        if (this._currentGameState!.roomAlias !== "startup") {
            return html`
                <game-map></game-map>
            `;
        }
        // Else if player is in the start-up room, then render this:
        else {
            return html`
                <game-account></game-account>
                `;
        }
    }

    /**
     * Switch to a different page
     *
     * @param page Page to switch to
     */
    private handleSwitchPage(page: Page): void {
        console.log("➡️ Switching page to:", page);

        this._currentPage = page;
        void this.render();
    }
}
